import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { useAppStore } from '../../store/appStore';

// Chart color palette for dark theme
const COLORS = {
  primary: '#F5D547',
  secondary: '#10b981',
  tertiary: '#3b82f6',
  quaternary: '#8b5cf6',
  accent1: '#f59e0b',
  accent2: '#ec4899'
};

const PIE_COLORS = ['#F5D547', '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899'];

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-recipe-card border border-recipe-border rounded-lg p-3 shadow-xl">
        <p className="text-recipe-text font-semibold mb-1">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AnalyticsDashboard() {
  const {
    dailyInteractions,
    appUsageByCategory,
    engagementMetrics,
    performanceData
  } = useAppStore();

  // Prepare data for the line chart (AI interactions over 7 days)
  const interactionsChartData = dailyInteractions.map((value, index) => ({
    day: `Day ${index + 1}`,
    interactions: value,
    date: new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  // Calculate total for pie chart percentage
  const totalEngagement = engagementMetrics.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="space-y-6">
      {/* AI Interactions Line Chart */}
      <div className="bg-recipe-card border border-recipe-border rounded-xl p-6">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-recipe-text">AI Interactions Trend</h3>
          <p className="text-sm text-recipe-text-muted mt-1">Daily AI interactions over the past 7 days</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={interactionsChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2f2a" />
            <XAxis
              dataKey="date"
              stroke="#a0a0a0"
              tick={{ fill: '#a0a0a0' }}
              tickLine={{ stroke: '#2a2f2a' }}
            />
            <YAxis
              stroke="#a0a0a0"
              tick={{ fill: '#a0a0a0' }}
              tickLine={{ stroke: '#2a2f2a' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ color: '#e5e5e5' }}
              iconType="circle"
            />
            <Line
              type="monotone"
              dataKey="interactions"
              stroke={COLORS.primary}
              strokeWidth={3}
              dot={{ fill: COLORS.primary, r: 5 }}
              activeDot={{ r: 7 }}
              animationDuration={1500}
              name="Interactions"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Two column layout for Bar and Pie charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* App Usage Bar Chart */}
        <div className="bg-recipe-card border border-recipe-border rounded-xl p-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-recipe-text">App Usage by Category</h3>
            <p className="text-sm text-recipe-text-muted mt-1">Total usage across categories</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={appUsageByCategory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2f2a" />
              <XAxis
                dataKey="category"
                stroke="#a0a0a0"
                tick={{ fill: '#a0a0a0', fontSize: 11 }}
                tickLine={{ stroke: '#2a2f2a' }}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="#a0a0a0"
                tick={{ fill: '#a0a0a0' }}
                tickLine={{ stroke: '#2a2f2a' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="count"
                fill={COLORS.primary}
                radius={[8, 8, 0, 0]}
                animationDuration={1500}
                name="Usage Count"
              >
                {appUsageByCategory.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* User Engagement Pie Chart */}
        <div className="bg-recipe-card border border-recipe-border rounded-xl p-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-recipe-text">User Engagement</h3>
            <p className="text-sm text-recipe-text-muted mt-1">Breakdown by user type</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={engagementMetrics}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                animationDuration={1500}
              >
                {engagementMetrics.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {engagementMetrics.map((metric, index) => (
              <div key={metric.name} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                />
                <span className="text-xs text-recipe-text-muted">
                  {metric.name}: <span className="text-recipe-text font-semibold">{metric.value}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Performance Area Chart */}
      <div className="bg-recipe-card border border-recipe-border rounded-xl p-6">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-recipe-text">System Performance</h3>
          <p className="text-sm text-recipe-text-muted mt-1">Real-time metrics over the last 24 hours</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={performanceData}>
            <defs>
              <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorMemory" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorLatency" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.tertiary} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLORS.tertiary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2f2a" />
            <XAxis
              dataKey="time"
              stroke="#a0a0a0"
              tick={{ fill: '#a0a0a0', fontSize: 11 }}
              tickLine={{ stroke: '#2a2f2a' }}
              interval={3}
            />
            <YAxis
              stroke="#a0a0a0"
              tick={{ fill: '#a0a0a0' }}
              tickLine={{ stroke: '#2a2f2a' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ color: '#e5e5e5' }}
              iconType="circle"
            />
            <Area
              type="monotone"
              dataKey="cpu"
              stroke={COLORS.primary}
              fillOpacity={1}
              fill="url(#colorCpu)"
              strokeWidth={2}
              animationDuration={1500}
              name="CPU %"
            />
            <Area
              type="monotone"
              dataKey="memory"
              stroke={COLORS.secondary}
              fillOpacity={1}
              fill="url(#colorMemory)"
              strokeWidth={2}
              animationDuration={1500}
              name="Memory %"
            />
            <Area
              type="monotone"
              dataKey="latency"
              stroke={COLORS.tertiary}
              fillOpacity={1}
              fill="url(#colorLatency)"
              strokeWidth={2}
              animationDuration={1500}
              name="Latency (ms)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
